import { Box, ResponsiveContext, Spinner, Text } from 'grommet';
import { useSession } from 'next-auth/react';
import { useRouter } from 'next/router';
import React, {useContext, useEffect, useMemo, useRef, useState} from 'react';
import { useSWRConfig } from 'swr';
import { handleErrors, handleFileDownloadErrors } from '../../../utils/errors';
import { formatCurrency } from '../../../utils/numbers';
import Badge from '../../Badge';
import CustomerLabel from '../../CustomerLabel';
import CustomerTag from '../../CustomerTag';
import DataTableWithBackendSearch from '../../DataTableWithBackendSearch';
import { DialogContext } from '../../DialogProvider';
import ScheduleSendModal from '../../draft-invoices/ScheduleSendModal';
import Modal from '../../Modal';
import OrderTag from '../../OrderTag';
import {
    faTrashCan as IconDelete,
    faSend as IconSend,
    faClock as IconTime,
} from '@fortawesome/pro-light-svg-icons';
import FontAwesomeIcon from '../../FontAwesomeIcon';
import { downloadBlobAsCsvFile } from '../../../lib/exportcsv';
import { csvFilename } from '../../../utils/csv';
import { formatName } from '../../../utils/names';
import { getQueryString } from '../../../utils/search';
import { NotificationsContext } from '../../NotificationsProvider';
import { formatDate, formatTime } from '../../../utils/dates';
import Button from '../../Button';
import { setDraftInvoiceNavigationData, setDraftInvoiceBackUrl } from '../../../utils/invoiceNavigation';

const POLLING_INTERVAL = 3000; // Poll every 3 seconds while invoicing

const OrdersList = ({setOrdersQueryUrl, onOrderListChange, hasShopifyLinked}) => {
    const router = useRouter();
    const { mutate } = useSWRConfig();
    const resetSelectedRowsRef = useRef(null);
    const [selectedOrders, setSelectedOrders] = useState([]);
    const currentQueryUrl = useRef(null);
    const notificationCtx = useContext(NotificationsContext);
    const [scheduleSendModal, setScheduleSendModal] = useState(false);
    const [csvQueryUrl, setCsvQueryUrl] = useState(); // Used for exporting csv with correct params
    const [showBulkInvoiceModal, setShowBulkInvoiceModal] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [invoicingOrderIds, setInvoicingOrderIds] = useState([]);
    const {data: session} = useSession();
    const {company} = session || {};
    const size = useContext(ResponsiveContext);
    const isMobile = size === "small";

    const refreshData = () => {
        if (currentQueryUrl.current) {
            mutate(currentQueryUrl.current);
        }
    };

    // Poll for updates while there are invoicing orders
    useEffect(() => {
        if (invoicingOrderIds.length === 0) return;

        const interval = setInterval(() => {
            refreshData();
        }, POLLING_INTERVAL);

        return () => clearInterval(interval);
    }, [invoicingOrderIds.length]);

    const columns = useMemo(() => {
        return isMobile ? [
            {
                selector: row => row.id,
                cell: (datum) => {
                    return (
                        <Box data-tag="allowRowEvents" fill="horizontal" justify="between" direction="row" gap="small" align="center" pad={{right: 'xsmall'}}>
                            <Box data-tag="allowRowEvents">
                                <CustomerLabel data-tag="allowRowEvents" customerId={datum?.recipient_id} />
                                <Text data-tag="allowRowEvents" size="small" color="text-weak">Invoice {datum?.identifier}{datum?.draft_series_id ? ' (Recurring)' : ''}</Text>
                            </Box>
                            <Box data-tag="allowRowEvents">
                                {formatCurrency(datum?.total_amount, datum?.currency)}
                            </Box>
                        </Box>
                    )
                },
            }
        ] : [
            {
                selector: row => row.customer_name,
                name: "Customer",
                sortable: true,
                sortField: 'company_name',
                cell: (datum) => datum?.recipient_id && (<CustomerTag id={datum.recipient_id} inline />),
                grow: 2,
            },
            {
                selector: row => row.identifier,
                name: "Invoice #",
                sortable: true,
                sortField: 'identifier',
                cell: (datum) => (<OrderTag id={datum?.id} />)
            },
            {
                selector: row => !!row.draft_series_id,
                name: "Type",
                grow: 0.5,
                cell: (datum) => {
                    if (datum.isInvoicing) {
                        return (
                            <Box direction="row" align="center" gap="xsmall">
                                <Spinner size="xsmall" />
                                <Text size="small" color="text-weak">Issuing...</Text>
                            </Box>
                        );
                    }

                    const isRecurring = !!datum.draft_series_id;
                    const isMilestoneInvoice = datum?.has_billing_milestones;

                    let badgeColor = "type-single-invoice";
                    let type = "Single";
                    if (isRecurring) {
                        badgeColor = "type-recurring-invoice";
                        type = "Recurring";
                    }
                    if (isMilestoneInvoice) {
                        badgeColor = "type-milestone-invoice";
                        type = "Split";
                    }
                    return (
                        <Badge background={badgeColor}>
                            {type}
                        </Badge>
                    )
                },
            },
            {
                selector: row => row.total_amount,
                name: "Amount",
                sortable: true,
                right: true,
                type: "currency",
                sortField: 'total_amount',
                cell: (datum) => formatCurrency(datum.total_amount, datum.currency),
            },
            {
                selector: row => row.created_at,
                name: "Created at",
                sortable: true,
                sortField: 'created_at',
                cell: (datum) => datum.formatted_created_at,
                grow: 1.5,
            },
            {
                selector: row => row.updated_at,
                name: "Last updated at",
                sortable: true,
                sortField: 'updated_at',
                cell: (datum) => datum.formatted_updated_at,
                grow: 1.5
            },
        ];
    }, [isMobile]);

    const archiveOrders = () => {
        const orderIds = selectedOrders.map((order) => order.id).join(",");
        fetch(`/api/draft-invoices/${orderIds}`, {
            method: "DELETE",
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
            },
        })
            .then(handleErrors)
            .then(
                notificationCtx.showNotification({
                    message: `${selectedOrders.length} draft${selectedOrders.length > 1 ? 's' : ''} archived!`,
                })
            )
            .then(onOrderListChange)
            .catch(notificationCtx.showNotification);
    };

    const bulkInvoiceOrders = async (excludeEmail=false) => {
        const orderIds = selectedOrders.map((order) => order.id).join(",");
        setIsSubmitting(true);

        try {
            const response = await fetch(`/api/draft-invoices/bulk`, {
                method: "POST",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    ids: orderIds,
                    ...(excludeEmail ? {exclude_email: 1} : {})
                }),
            });
            await handleErrors(response);

            notificationCtx.showNotification({
                message: `${selectedOrders.length > 1 ? 'Invoices are' : 'Invoice is'} being issued...`,
            });
            setShowBulkInvoiceModal(false);
            resetSelectedRowsRef.current();
            // Refresh to get updated invoicing_order_ids
            refreshData();
        } catch (err) {
            notificationCtx.showNotification({
                type: 'error',
                message: 'Failed to start invoicing. Please try again.',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const bulkSendLater = async (date) => {
        const orderIds = selectedOrders.map((order) => order.id).join(",");
        setIsSubmitting(true);

        try {
            const response = await fetch(`/api/draft-invoices/bulk`, {
                method: "PUT",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ids: orderIds, scheduled: true, issued_at: new Date(date)}),
            });
            const res = await handleErrors(response);

            let message = `Invoice${selectedOrders.length > 1 ? 's' : ''} scheduled!`;
            if (res?.errors?.length) {
                message += ` Failed: ${res.errors.map(ord => ord.order_identifier).join(', ')}`;
            }

            notificationCtx.showNotification({ message });
            setScheduleSendModal(false);
            resetSelectedRowsRef.current();
            onOrderListChange();
        } catch (err) {
            notificationCtx.showNotification({
                type: 'error',
                message: 'Failed to schedule invoices. Please try again.',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const getDraftInvoiceCSV = ({callback, url}) => {
        const exportError = {type: 'error', message: 'Unable to download csv data. Try again later.'};
        fetch(url, {
            method: 'GET'
        })
            .then(handleFileDownloadErrors)
            .then((data) => {
                data ?
                    downloadBlobAsCsvFile(
                        data,
                        csvFilename('draft_invoice_export', { businessName: company?.business_name || company?.legal_name })) :
                    notificationCtx.showNotification(exportError);
                callback && callback();
            })
            .catch(e => notificationCtx.showNotification(exportError));
    };

    return (
        <>
            {scheduleSendModal && (
                <ScheduleSendModal
                    onSubmit={bulkSendLater}
                    onClose={() => setScheduleSendModal(false)}
                    isLoading={isSubmitting}
                />
            )}
            {showBulkInvoiceModal && (
                <Modal
                    title={`Issue ${selectedOrders.length > 1 ? 'invoices': 'invoice'}`}
                    showSpinnerWhenProcessing={true}
                    onClose={() => !isSubmitting && setShowBulkInvoiceModal(false)}>
                    <Box pad={{vertical: 'small'}} width={{max: 'medium'}}>
                        { `Are you sure you would like to ${
                            selectedOrders.length > 1 ? 'invoice the': 'send an invoice for the'
                        }
                        ${selectedOrders.length > 1 ? selectedOrders.length : ''} selected ${
                    selectedOrders.length > 1
                        ? "invoice drafts"
                        : "invoice draft"}?`}
                    </Box>
                    <Box align="center" direction="row" justify="end" pad={{top: 'medium'}}>
                        <Box direction="row" align="center" gap="small">
                            <Button
                                event={{section: 'order_list', element: 'bulk_issue_invoices_link_only'}}
                                secondary
                                disabled={isSubmitting}
                                label={`Issue`}
                                onClick={() => bulkInvoiceOrders(true)} />
                            <Button
                                event={{section: 'order_list', element: 'bulk_issue_invoices_and_send_email'}}
                                primary
                                disabled={isSubmitting}
                                label={isSubmitting ? 'Submitting...' : `Issue & send email${selectedOrders.length > 1 ? 's' : ''}`}
                                onClick={() => bulkInvoiceOrders()} />
                        </Box>
                    </Box>
                </Modal>
            )}
            <DialogContext.Consumer>
                {({ showDialog }) => (
                    <DataTableWithBackendSearch
                        syncFiltersToUrl
                        onRowClicked={(row) => {
                            setDraftInvoiceBackUrl(router.asPath);
                            router.push(`/draft-invoices/${row.id}`);
                        }}
                        columns={columns}
                        emptyListMessage="No draft invoices to review"
                        dataName="invoices"
                        tab="draft"
                        noTableHead={isMobile}
                        setQueryUrlCallback={(url) => { // get query url from since data table controls the state of filters and sorting
                            const queryParams = url.split("?")[1]; // get only the url
                            setCsvQueryUrl("/api/draft-invoices/csv?" + queryParams);
                            setOrdersQueryUrl(url);
                            currentQueryUrl.current = url; // Store for navigation
                        }}
                        onDataChange={(orders, rawData) => {
                            if (orders && orders.length > 0) {
                                const tokens = orders.map(order => order.id);
                                const total = rawData?.total || orders.length;
                                const currentOffset = parseInt(new URLSearchParams(currentQueryUrl.current?.split('?')[1] || '').get('offset') || '0');
                                const hasMore = (currentOffset + orders.length) < total;
                                setDraftInvoiceNavigationData({
                                    tokens,
                                    queryUrl: currentQueryUrl.current,
                                    hasMore,
                                    total
                                });
                            }
                        }}
                        reshape={(data) => {
                            if (!data) return null;
                            const invoicingIds = data.invoicing_order_ids || [];
                            // Update state with current invoicing order IDs
                            setInvoicingOrderIds(invoicingIds);
                            return data.orders.map((order) => ({
                                ...order,
                                created_at: new Date(order.created_at),
                                formatted_created_at: `${formatDate(order.created_at, false, true)}, ${formatTime(order.created_at)}`,
                                formatted_updated_at: `${formatDate(order.updated_at, false, true)}, ${formatTime(order.updated_at)}`,
                                company_name: order.recipient
                                    ? order.recipient.company_name
                                    : "-",
                                customer_name: formatName(
                                    order.recipient,
                                    true,
                                    "-"
                                ),
                                order_number: order.identifier,
                                isInvoicing: invoicingIds.includes(order.id),
                            }));
                        }}
                        getUrl={(props) =>
                            getQueryString({
                                ...props,
                                baseUrl: "api/draft-invoices",
                            })
                        }
                        selectableRows
                        selectableRowDisabled={row => !!row.draft_series_id || row.isInvoicing}
                        selectedControls={
                            <Box direction="row" gap="xsmall" justify="end">
                                <Box>
                                    <Button
                                        event={{section: 'order_list', element: 'bulk_archive'}}
                                        secondary
                                        icon={
                                            <FontAwesomeIcon
                                                SelectedIcon={IconDelete}
                                                color="status-critical"
                                                size="xsmall"
                                            />
                                        }
                                        label={<Text color="status-critical">Archive</Text>}
                                        gap="xsmall"
                                        onClick={() => {
                                            showDialog({
                                                title: "Archive orders",
                                                message: `Are you sure you would like to archive ${
                                                    selectedOrders.length
                                                } ${
                                                    selectedOrders.length > 1
                                                        ? "drafts"
                                                        : "draft"
                                                }?`,
                                                callback: archiveOrders,
                                                ctaType: 'error'
                                            });
                                        }}
                                        hoverIndicator
                                        align="end"
                                    />
                                </Box>
                                {!isMobile && (<Box>
                                    <Button
                                        event={{section: 'order_list', element: 'bulk_send_later'}}
                                        secondary
                                        icon={
                                            <Box margin={{right: 'xxsmall'}}>
                                                <FontAwesomeIcon
                                                    SelectedIcon={IconTime}
                                                    size="xsmall"
                                                />
                                            </Box>
                                        }
                                        label={<Text>Send later</Text>}
                                        gap="xsmall"
                                        onClick={() => setScheduleSendModal(true)}
                                        hoverIndicator
                                        align="end"
                                    />
                                </Box>)}
                                <Box>
                                    <Button
                                        event={{section: 'order_list', element: 'bulk_issue_invoices'}}
                                        icon={
                                            <FontAwesomeIcon
                                                SelectedIcon={IconSend}
                                                size="xsmall"
                                            />
                                        }
                                        secondary
                                        label={<Text>Issue {!isMobile && (selectedOrders.length > 1 ? 'invoices': 'invoice')}</Text>}
                                        gap="xsmall"
                                        onClick={() => setShowBulkInvoiceModal(true)}
                                        hoverIndicator
                                        align="end"
                                    />
                                </Box>
                            </Box>
                        }
                        onSelectedRowsChange={(data) =>
                            data?.selectedRows &&
                            setSelectedOrders(data.selectedRows)
                        }
                        onExportCSVClicked={() => getDraftInvoiceCSV({ url: csvQueryUrl })}
                        dataTableWrapperProps={{ margin: "none" }}
                        resetSelectedRowsRef={resetSelectedRowsRef}
                    />)}
            </DialogContext.Consumer>
        </>
    );
};

export default OrdersList;
